// ============================================================
// CombatLogOverlay - the map with the combat log hanging over it.
//
// The map fills the panel; the log hangs from its foot, rolled up to a
// one-line ticker, and opens upward over it (plan design-gaps L3).
// `.splitContainer` is `position: relative`, so the overlay needs no
// change to the shell around it. The log's edge is its TOP: the ticker,
// the bottom bar's four, the map fold, the roll-up toggle and the grip;
// the vox sits under the scroller, nearest the composer's hand.
//
// Split from CombatLogPanel, which composes the bars that lie over the map.
// ============================================================
import { useMemo, useRef } from 'react';
import clsx from '../utils/clsx';
import styles from '../styles/CombatLogPanel.module.css';
import { LocalLogEntries, SharedLogEntries } from './CombatLogEntries.jsx';
import ChatChips from './ChatChips';
import ChatComposer from './ChatComposer';
import LaneChips from './log/LaneChips';
import useLogRead from '../hooks/useLogRead';
import useLogFoot from '../hooks/useLogFoot';
import CappedBars from './CappedBars';
import { tickerLine } from '../engine/log/ticker';

/** No lane switched off: the stable default, so the entries' memo holds. */
const NO_LANES = [];
const NO_CHAT = [];
const NOBODY = { uid: null, name: null };

const LOG_VIEWS = [
  { key: 'shared', label: 'Shared' },
  { key: 'local', label: 'Local' },
];

/** How many lines each view holds: the shared log, the chat, and the target's own log. */
function lineCounts(props, chat) {
  return `${props.firestoreNarrative?.length ?? 0}:${chat.length}:${props.currentTab?.narrativeLog?.length ?? 0}`;
}

/** What a chat line needs of the vox, plus the director's pin. Null outside a session. */
function voxFor(vox, pin) {
  if (!vox) return null;
  return { me: vox.me, isGm: vox.isGm, people: vox.people, openDm: vox.openDm, remove: vox.remove, pin };
}

/** While scrolled up with new lines below: how many, and the way to them. */
function JumpPill({ read }) {
  if (read.atBottom || read.unseenShown === 0) return null;
  return (
    <button type="button" className={styles.logJump} data-testid="log-jump-new" onClick={read.jump}>
      {read.unseenShown} new ↓
    </button>
  );
}

function LogScroller({ p, wrapRef }) {
  const { vox, view, onLookAt, read } = p;
  return (
    <div className={styles.logScrollWrap} hidden={p.collapsed} ref={wrapRef}>
      {p.sessionId && view === 'shared' && <LaneBar p={p} />}
      <div className={styles.logScroller} ref={p.narrativeRef} role="tabpanel" aria-live="polite">
        {view === 'shared' ? (
          <SharedLogEntries
            entries={p.firestoreNarrative}
            onLookAt={onLookAt}
            chat={vox?.messages ?? NO_CHAT}
            hidden={p.logLanesOff ?? NO_LANES}
            vox={voxFor(vox, p.onPinLine)}
            readAt={read.readAt}
            reader={p.reader}
            expanded={p.logExpandAll}
          />
        ) : (
          <LocalLogEntries log={p.currentTab?.narrativeLog || []} onLookAt={onLookAt} expanded={p.logExpandAll} />
        )}
      </div>
      {view === 'shared' && <JumpPill read={read} />}
    </div>
  );
}

/** Shared / Local. Discreet, on the log's edge. */
function LogViewBar({ view, setLogView }) {
  return (
    <div className={styles.logViewBar} role="tablist" aria-label="Combat log views">
      {LOG_VIEWS.map((v) => (
        <button
          key={v.key}
          role="tab"
          data-testid={'log-view-' + v.key}
          aria-selected={view === v.key}
          className={clsx(styles.logViewTab, view === v.key && styles.logViewTabActive)}
          onClick={() => setLogView(v.key)}
        >
          {v.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The lanes of the shared log (which kinds of line it shows, and what is
 * new in each) and whether area attacks are drawn open: a row of its own
 * over the entries. NOT on the edge: the edge is one pinned row
 * (`--log-edge-h`) that already holds the views, the grip and the roll-up
 * toggle, and six more buttons pushed the toggle out of reach (found by
 * the E2E run, 2026-10-06).
 */
function LaneBar({ p }) {
  return (
    <div className={styles.laneBar}>
      <LaneChips hidden={p.logLanesOff ?? NO_LANES} onToggle={p.toggleLogLane} counts={p.read.counts} />
      <button
        type="button"
        className={clsx(styles.logExpandAll, p.logExpandAll && styles.logExpandAllOn)}
        aria-pressed={!!p.logExpandAll}
        data-testid="log-expand-all"
        title={p.logExpandAll ? 'Draw each area attack as one line' : 'Open every area attack'}
        onClick={p.toggleLogExpandAll}
      >
        Expand all
      </button>
    </div>
  );
}

function CollapseToggle({ collapsed, onToggle, unread }) {
  return (
    <button
      type="button"
      className={styles.logToggle}
      data-testid="log-collapse"
      aria-expanded={!collapsed}
      aria-label={collapsed ? 'Show combat log' : 'Hide combat log'}
      onClick={onToggle}
    >
      {collapsed ? '▼' : '▲'}
      {unread > 0 && (
        <span className={styles.logUnread} data-testid="log-unread" aria-label={`${unread} unread`}>
          {unread}
        </span>
      )}
    </button>
  );
}

/** In a phase that folds the map (Narrative, Stealth): open it for yourself, or fold it away again. */
function MapFoldButton({ folded, onToggle }) {
  return (
    <button
      type="button"
      className={styles.mapFold}
      data-testid="map-fold"
      aria-pressed={!folded}
      title={folded ? 'Open the map on this screen' : 'Fold the map behind the log'}
      onClick={onToggle}
    >
      Map
    </button>
  );
}

/**
 * THE TICKER (plan map-display, E): the rolled-up log as its newest line; a click rolls it open. At the map's foot
 * it is the session page's "the log as a ticker (click it)": the label, the line, OPEN.
 */
function LogTicker({ p }) {
  return (
    <button type="button" className={styles.logTicker} data-testid="log-ticker" onClick={p.onToggleCollapsed}>
      <b>LOG</b>
      <span>
        {tickerLine(p.view === 'shared' ? p.firestoreNarrative : p.currentTab?.narrativeLog, p.logLanesOff ?? NO_LANES)}
      </span>
      <i data-testid="log-ticker-open">OPEN ▴</i>
    </button>
  );
}

/** The log's own buttons: unfold the map behind it, and roll it up or down (with the shared log's unread count). */
function EdgeControls({ p }) {
  return (
    <>
      {p.mapFold?.canFold && <MapFoldButton folded={p.folded} onToggle={p.mapFold.toggle} />}
      {!p.folded && (
        <CollapseToggle
          collapsed={p.collapsed}
          onToggle={p.onToggleCollapsed}
          unread={p.view === 'shared' ? p.read.unseen : 0}
        />
      )}
    </>
  );
}

/**
 * The switch and the handle share the overlay's edge: the switch
 * is a place you go rarely, and the edge is where you already reach to
 * resize. The switch appears only in a session - solo play has nothing
 * to share.
 */
function LogEdge({ p }) {
  return (
    <div className={styles.logEdge}>
      {p.sessionId && <LogViewBar view={p.view} setLogView={p.setLogView} />}
      {p.collapsed && <LogTicker p={p} />}
      {p.edgeActionsNode}
      {/* The log's own buttons sit with the actions, the grip at the right end (2026-10-22); an open panel never
          covers them, the log standing clear of it (`--map-inset-*`). */}
      <EdgeControls p={p} />
      {p.folded ? (
        <span className={styles.logEdgeFill} />
      ) : (
        <div
          className={styles.logGrip}
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize combat log"
          data-testid="log-resize"
          {...p.logResizeHandlers}
        />
      )}
    </div>
  );
}

/** THE VOX (v8.363.4): under the edge, so it shows even when the curtain is rolled up - always within reach. */
function Vox({ vox }) {
  return (
    <>
      <ChatChips
        conversations={vox.conversations}
        active={vox.active}
        unread={vox.unread}
        onSelect={(key) => {
          vox.setActive(key);
          vox.markRead(key);
        }}
        onClose={vox.closeDm}
      />
      <ChatComposer
        active={vox.active}
        conversations={vox.conversations}
        people={vox.people}
        channels={vox.channels}
        me={vox.me}
        actor={vox.actor}
        inCharacter={vox.inCharacter}
        onToggleInCharacter={() => vox.setInCharacter(!vox.inCharacter)}
        onSend={vox.send}
        error={vox.error}
        onClearError={vox.clearError}
        onFocusMap={() => document.querySelector('[data-testid="battlefield-focus"]')?.focus()}
      />
    </>
  );
}

/**
 * The map and the log over it. `p` carries the panel's layout props
 * (collapsed, logHeight, narrativeRef, logResizeHandlers, the view and
 * filter setters) plus the resolved `view`, the `vox`, the entries and
 * `mapFold` (see hooks/useMapFold).
 */
export default function CombatLogOverlay(props) {
  // FOLDED (v8.419.0): the log takes the whole panel and the map stays
  // mounted underneath. A rolled-up log would show the map, so folding
  // outranks it.
  const folded = !!props.mapFold?.folded;
  const collapsed = props.collapsed && !folded;
  const { vox } = props;
  // Who is reading: their own lines are never news to them (engine/log/readMark).
  const reader = useMemo(() => (vox?.me ? { uid: vox.me.uid, name: vox.me.name } : NOBODY), [vox?.me]);
  const chat = vox?.messages ?? NO_CHAT;
  const read = useLogRead({
    sessionId: props.sessionId,
    narrative: props.view === 'shared' ? props.firestoreNarrative : NO_CHAT,
    chat: props.view === 'shared' ? chat : NO_CHAT,
    reader,
    scrollerRef: props.narrativeRef,
    hidden: props.logLanesOff ?? NO_LANES,
    watching: props.view === 'shared' && !collapsed,
    // Anything that grows when a line arrives, on either view, or lays the scroller out again (a log
    // rolled down from nothing comes back scrolled to the top, and belongs at its foot).
    changeKey: `${collapsed}:${folded}:${props.view}:${lineCounts(props, chat)}`,
  });
  const p = { ...props, folded, collapsed, read, reader };
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const scrollWrapRef = useRef(null);
  useLogFoot({ containerRef, overlayRef, scrollWrapRef }, { folded, layoutKey: `${collapsed}:${props.view}` });
  return (
    <div
      ref={containerRef}
      className={styles.splitContainer}
      tabIndex={-1}
      data-testid="battlefield-focus"
      // Opened over the map, or the map folded behind it: the scene and room rails drop under it, so its
      // controls stay reachable.
      data-log-open={!collapsed || undefined}
    >
      <MapLayers p={p} />
      <LogOverlay p={p} overlayRef={overlayRef} scrollWrapRef={scrollWrapRef} />
    </div>
  );
}

/** The log itself, over the map: rolled up, opened to `logHeight`%, or folded over the whole panel. */
function LogOverlay({ p, overlayRef, scrollWrapRef }) {
  const { collapsed, folded } = p;
  return (
    <div
      ref={overlayRef}
      className={clsx(styles.logOverlay, collapsed && styles.logCollapsed, folded && styles.logFolded)}
      style={collapsed || folded ? undefined : { height: p.logHeight + '%' }}
      data-testid="log-overlay"
      // Hanging from the foot (plan design-gaps L3) the stylesheet puts the edge on TOP: the grip is reached from
      // above and the ticker is the foot's own line; the vox stays nearest the composer's hand.
      data-folded={folded || undefined}
    >
      {/* Folded, there is no map to lie over: the row stands in the log, under its edge (2026-10-23). */}
      {folded && <MapTopRow p={p} inLog />}
      <LogScroller p={p} wrapRef={scrollWrapRef} />
      <LogEdge p={p} />
      {p.vox && p.view === 'shared' && <Vox vox={p.vox} />}
    </div>
  );
}

/**
 * The map and what lies over it (plan design-gaps L2, L4, L5): one row along the map's top
 * edge holding the turn card, the story bars and the prompts, and the stealth strip, each placed by the row.
 */
function MapLayers({ p }) {
  return (
    <>
      {p.battlefieldNode}
      {!p.folded && <MapTopRow p={p} />}
    </>
  );
}

/**
 * The row: over the map's top edge, or - the map folded behind the log (Narrative, Stealth) - in the log under its
 * edge. The folded log is opaque and above the map, so a disposition to accept or a player's intent lay under it, out
 * of reach (2026-10-23). Always mounted where it stands: the bars' stack is always given, each empty or not.
 */
function MapTopRow({ p, inLog = false }) {
  const { turnCardNode, topBarsNode, stealthStripNode } = p;
  return (
    <div className={clsx(styles.mapTop, inLog && styles.mapTopInLog)}>
      {turnCardNode && (
        <div className={styles.turnCardOverMap} data-testid="turn-card-over-map">
          {turnCardNode}
        </div>
      )}
      <CappedBars>{topBarsNode}</CappedBars>
      {stealthStripNode}
    </div>
  );
}
